/**
 * Search (design-handoff/components/search.md, behaviour.md §5; Shopping v13 srch(), ported).
 *
 * - The catalogue: /collections/all?view=cw-index, every product as its aisle card (one per
 *   family), fetched once per page when search opens. Rows are made from those cards (their
 *   own ADD control, so a row adds exactly as the card does); grids show the cards themselves.
 * - Ranking, in the browser, the design's way: every query word must match (AND); a word also
 *   tries its synonym (data/search-synonyms.json; a 4+ letter word one typo from a synonym key
 *   counts); name: first word 6, word 5, prefix 4, near miss 3 (one typo, two from 7 letters);
 *   cue / chip / aisle: word or prefix 2, near miss 1 (5+ letters). Grind and pack words are not
 *   searched. "Did you mean": up to 4 catalogue words within two typos.
 * - Phone: a full-screen overlay with its own input and basket bar. Tablet: the overlay sits
 *   under the 64 header and the header's field types into it ("Cancel" closes). Desktop: a
 *   560 x 620 dropdown under the header field (idle, or aisles + top 5 + "See all"); Enter or
 *   "See all" opens /search?q=, drawn by <cw-search-page> with the same ranking.
 * - Recent searches: the last 5, kept in this browser when a result is opened or added.
 * - A search that asks for a mix ("mix", "multigrain", "custom", "blend"…) gets the custom mix
 *   builder first (snippets/cw-mix-tile, in snippets/cw-search): a row in lists, the tile in
 *   grids; on its own when nothing else matches.
 */
import { cart } from '@cw/cart';
import { AISLES } from '@cw/aisles';
import { esc, rupees } from '@cw/products';

const read = (id) => {
  try {
    return JSON.parse(document.getElementById(id)?.textContent || 'null');
  } catch {
    return null;
  }
};
const SYN = read('cw-search-words')?.synonyms || {};
const SYN_KEYS = Object.keys(SYN);
const RECENT_KEY = 'cw:search:recent';
const REQ_KEY = 'cw:search:requested';
const root = () => window.Shopify?.routes?.root || '/';
const PHONE = window.matchMedia('(max-width: 599.98px)');
const DESK = window.matchMedia('(min-width: 1024px)');

/** v13 lev(): edit distance, 9 when the lengths differ by more than 2. */
export function lev(a, b) {
  if (Math.abs(a.length - b.length) > 2) return 9;
  const d = [];
  for (let i = 0; i <= a.length; i++) d[i] = [i];
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
  }
  return d[a.length][b.length];
}
const words = (h) =>
  String(h || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

/** v13 srch(). */
export function rank(items, q) {
  const qt = words(q);
  if (!qt.length) return [];
  const variants = qt.map((t) => {
    const v = [t];
    if (SYN[t]) v.push(SYN[t]);
    else if (t.length >= 4) {
      const k = SYN_KEYS.find((x) => lev(t, x) <= 1);
      if (k) v.push(SYN[k]);
    }
    return v;
  });
  const out = [];
  for (const item of items) {
    let score = 0;
    let ok = true;
    for (const vs of variants) {
      let best = 0;
      for (const v of vs) {
        item.nameWords.forEach((w, i) => {
          if (w === v) best = Math.max(best, i === 0 ? 6 : 5);
          else if (w.startsWith(v)) best = Math.max(best, 4);
          else if (v.length >= 4 && lev(v, w) <= (v.length >= 7 ? 2 : 1)) best = Math.max(best, 3);
        });
        for (const w of item.otherWords) {
          if (w === v || w.startsWith(v)) best = Math.max(best, 2);
          else if (v.length >= 5 && lev(v, w) <= 1) best = Math.max(best, 1);
        }
      }
      if (!best) ok = false;
      score += best;
    }
    if (ok) out.push([score, item]);
  }
  return out.sort((a, b) => b[0] - a[0]).map((x) => x[1]);
}

/** v13: up to 4 catalogue words within two typos; the same first two letters come first. */
export function didYouMean(items, q) {
  const vocab = new Set();
  for (const item of items) for (const w of words(item.name)) if (w.length >= 4 && !/\d/.test(w)) vocab.add(w);
  const tk = String(q).toLowerCase().split(/\s+/).filter(Boolean);
  if (!tk.length) return [];
  return [...vocab]
    .map((w) => [Math.min(...tk.map((t) => lev(t, w) - (w.slice(0, 2) === t.slice(0, 2) ? 0.5 : 0))), w])
    .filter((x) => x[0] <= 2)
    .sort((a, b) => a[0] - b[0])
    .slice(0, 4)
    .map((x) => x[1]);
}

let catalogue = null;
/** The catalogue's items: {id, card, name, url, aisle, pack, price, mrp, image, nameWords, otherWords}. */
export function loadCatalogue() {
  if (catalogue) return catalogue;
  catalogue = fetch(`${root()}collections/all?view=cw-index`, { headers: { Accept: 'text/html' } })
    .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`index ${response.status}`))))
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const box = doc.querySelector('[data-cw="catalogue"]');
      const store = document.createElement('div');
      store.hidden = true;
      store.dataset.cw = 'catalogue';
      if (box) store.append(...box.childNodes);
      document.body.append(store);
      return [...store.children].filter((el) => el.classList.contains('cw-card')).map(itemOf).filter(Boolean);
    })
    .catch((error) => {
      catalogue = null;
      throw error;
    });
  return catalogue;
}

function itemOf(card) {
  const id = card.dataset.productId;
  let data = null;
  try {
    data = JSON.parse(card.querySelector(`script[data-cw-product="${id}"]`)?.textContent || 'null');
  } catch {
    data = null;
  }
  if (!data) return null;
  const nameEl = card.querySelector('.cw-card__name');
  const name = nameEl?.textContent.trim() || data.title;
  // A family card is found by any member's name too (Multigrain Soft / Medium / Hard).
  const members = [...card.querySelectorAll('script[data-cw-product]')]
    .map((s) => {
      try {
        return JSON.parse(s.textContent);
      } catch {
        return null;
      }
    })
    .filter(Boolean);
  const aisle = AISLES.find((a) => a.id === data.aisle);
  const price = card.querySelector('.cw-card__price')?.textContent.trim() || '';
  const mrp = card.querySelector('.cw-card__mrp')?.lastChild?.textContent.trim() || '';
  return {
    id,
    card,
    name,
    url: nameEl?.getAttribute('href') || data.url,
    aisle: data.aisle,
    aisleShort: aisle?.short || '',
    aisleName: aisle?.name || '',
    pack: card.querySelector('.cw-card__tag')?.textContent.trim() || '',
    price,
    mrp,
    image: data.image,
    nameWords: words([name, ...members.map((m) => m.title)].join(' ')),
    otherWords: words([data.cue, data.chip, aisle?.name, ...members.map((m) => m.cue)].filter(Boolean).join(' ')),
  };
}

function loadList(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]') || [];
  } catch {
    return [];
  }
}
function saveList(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // kept for this page only
  }
}
export function remember(q) {
  const t = String(q || '').trim();
  if (!t) return;
  saveList(RECENT_KEY, [t, ...loadList(RECENT_KEY).filter((r) => r.toLowerCase() !== t.toLowerCase())].slice(0, 5));
}

const ICONS = {
  clock:
    '<svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M128 20a108 108 0 1 0 108 108A108.12 108.12 0 0 0 128 20m0 192a84 84 0 1 1 84-84a84.09 84.09 0 0 1-84 84m68-84a12 12 0 0 1-12 12h-56a12 12 0 0 1-12-12V72a12 12 0 0 1 24 0v44h44a12 12 0 0 1 12 12"/></svg>',
};

/** One result row, made from the product's card (its ADD control is the card's own). */
export function rowOf(item) {
  const row = document.createElement('div');
  row.className = 'cw-sr__row';
  row.innerHTML = `<a class="cw-sr__thumb" href="${esc(item.url)}" data-result>${item.image ? `<img src="${esc(item.image)}" alt="" loading="lazy">` : ''}</a>
    <div class="cw-sr__text">
      <a class="cw-sr__name" href="${esc(item.url)}" data-result>${esc(item.name)}</a>
      <div class="cw-sr__sub">${esc([item.aisleShort, item.pack].filter(Boolean).join(' · '))}</div>
      <div class="cw-sr__price"><span>${esc(item.price)}</span>${item.mrp ? `<span class="cw-sr__mrp"><span class="cw-sr">MRP </span>${esc(item.mrp)}</span>` : ''}</div>
    </div>`;
  const add = item.card.querySelector('cw-add, .cw-add--soldout');
  if (add) row.append(add.cloneNode(true));
  return row;
}

/** The custom mix builder: wanted when a query word starts (or is the start of) a mix word. */
const MIX_WORDS = ['mix', 'mixed', 'multigrain', 'multi', 'custom', 'blend', 'build', 'own'];
const mixTile = () => document.getElementById('cw-mix-entry')?.content.querySelector('.cw-card') || null;
export function wantsMix(q) {
  if (!mixTile()) return false;
  return words(q)
    .filter((w) => w.length >= 3)
    .some((w) => MIX_WORDS.some((m) => m.startsWith(w) || w.startsWith(m)));
}
/** The builder as a list row: its picture, name and line, BUILD in the ADD button's place. */
function mixRow() {
  const tile = mixTile();
  if (!tile) return null;
  const url = tile.querySelector('.cw-card__name')?.getAttribute('href') || '/pages/custom-mix';
  const name = tile.querySelector('.cw-card__name')?.textContent.trim() || '';
  const line = tile.querySelector('.cw-card__cue')?.textContent.trim() || '';
  const pic = tile.querySelector('.cw-card__img, .cw-mixtile__mill')?.getAttribute('src') || '';
  const row = document.createElement('div');
  row.className = 'cw-sr__row cw-sr__row--mix';
  row.innerHTML = `<a class="cw-sr__thumb cw-sr__thumb--mix" href="${esc(url)}" data-result>${pic ? `<img src="${esc(pic)}" alt="" loading="lazy">` : ''}</a>
    <div class="cw-sr__text">
      <a class="cw-sr__name" href="${esc(url)}" data-result>${esc(name)}</a>
      <div class="cw-sr__sub">${esc(line)}</div>
      <div class="cw-sr__mixprice">Priced by your grains</div>
    </div>
    <a class="cw-mixtile__go" href="${esc(url)}" aria-label="${esc(name)}">BUILD</a>`;
  return row;
}

/** Aisle tiles ("BROWSE AISLES" and no-results). */
const aisleTiles = () =>
  AISLES.map(
    (a) => `<a class="cw-sr__aisle" href="${esc(a.url)}"><span class="cw-sr__aisleicon" style="background-image:url('${esc(a.icon)}')"></span><span>${esc(a.short)}</span></a>`
  ).join('');

/** The search states, drawn into `box` (overlay, dropdown and page share them). */
export class SearchView {
  constructor(box, { mode, head = null }) {
    this.box = box;
    this.head = head; // page: the top row takes the count and the aisle chips
    this.mode = mode; // 'overlay' | 'dropdown' | 'page'
    this.aisle = null;
    this.items = [];
  }

  async draw(q) {
    this.q = String(q || '').trim();
    try {
      this.items = await loadCatalogue();
    } catch {
      this.box.innerHTML = '<div class="cw-sr__none"><div class="cw-sr__nonetext">Search is not answering just now. Try again in a moment.</div></div>';
      return;
    }
    if (!this.q) return this.#idle();
    const found = rank(this.items, this.q);
    if (!found.length) return wantsMix(this.q) ? this.#mixOnly() : this.#none();
    this.#results(found);
  }

  /** Nothing in the catalogue, but the words ask for a mix: the builder on its own. */
  #mixOnly() {
    const grid = this.mode !== 'dropdown' && !PHONE.matches;
    if (this.head) this.head.innerHTML = '';
    this.box.innerHTML = `<div class="cw-sr__stack cw-sr__stack--results">
        ${grid ? '<div class="cw-grid cw-sr__grid" data-rows="results"></div>' : '<div class="cw-sr__rows" data-rows="results"></div>'}
      </div>`;
    const list = this.box.querySelector('[data-rows="results"]');
    const entry = grid ? mixTile()?.cloneNode(true) : mixRow();
    if (entry) list.append(entry);
  }

  #idle() {
    const recent = loadList(RECENT_KEY);
    const history = new Set((read('cw-history') || []).map((h) => String(h.id)));
    const bought = this.items.filter((i) => history.has(String(i.id))).slice(0, 6);
    this.box.innerHTML = `<div class="cw-sr__stack">
        ${
          recent.length
            ? `<div class="cw-sr__group"><div class="cw-sr__headrow"><div class="cw-sr__eyebrow">RECENT SEARCHES</div><button type="button" class="cw-sr__clear" data-clear-recent>Clear</button></div>
                <div class="cw-sr__chips">${recent.map((r) => `<button type="button" class="cw-sr__recent" data-query="${esc(r)}">${ICONS.clock}<span>${esc(r)}</span></button>`).join('')}</div></div>`
            : ''
        }
        ${bought.length ? '<div class="cw-sr__group"><div class="cw-sr__eyebrow">BOUGHT EARLIER</div><div class="cw-sr__rows" data-rows="bought"></div></div>' : ''}
        <div class="cw-sr__group"><div class="cw-sr__eyebrow">BROWSE AISLES</div><div class="cw-sr__aisles">${aisleTiles()}</div></div>
      </div>`;
    const box = this.box.querySelector('[data-rows="bought"]');
    if (box) box.append(...bought.map(rowOf));
  }

  #none() {
    const requested = loadList(REQ_KEY).includes(this.q.toLowerCase());
    const dm = didYouMean(this.items, this.q);
    this.box.innerHTML = `<div class="cw-sr__stack">
        <div class="cw-sr__nonehead"><div class="cw-sr__nonetitle">No match for “${esc(this.q)}”</div><div class="cw-sr__nonetext">Try a shorter word, or its Hindi or English name.</div></div>
        ${dm.length ? `<div class="cw-sr__group"><div class="cw-sr__eyebrow">DID YOU MEAN</div><div class="cw-sr__chips">${dm.map((w) => `<button type="button" class="cw-sr__dm" data-query="${esc(w)}">${esc(w)}</button>`).join('')}</div></div>` : ''}
        <div class="cw-sr__request">
          <div class="cw-sr__reqtext"><div class="cw-sr__reqtitle">Can’t find it?</div><div class="cw-sr__reqsub">Tell us and we’ll try to stock it at the mill.</div></div>
          ${requested ? '<span class="cw-sr__reqdone">Requested ✓</span>' : '<button type="button" class="cw-sr__reqbtn" data-request>REQUEST</button>'}
        </div>
        <div class="cw-sr__group"><div class="cw-sr__eyebrow">BROWSE AISLES</div><div class="cw-sr__aisles">${aisleTiles()}</div></div>
      </div>`;
  }

  #results(found) {
    const aisles = [];
    for (const item of found) if (item.aisle && !aisles.includes(item.aisle)) aisles.push(item.aisle);
    if (this.aisle && !aisles.includes(this.aisle)) this.aisle = null;
    const shown = this.aisle ? found.filter((i) => i.aisle === this.aisle) : found;
    const count = `${found.length} result${found.length === 1 ? '' : 's'}`;

    if (this.mode === 'dropdown') {
      // Desktop v3: the count, one pill per aisle in the results, the top 5, "See all".
      const links = aisles
        .map((id) => AISLES.find((a) => a.id === id))
        .filter(Boolean)
        .map((a) => `<a class="cw-sr__dkaisle" href="${esc(a.url)}">${esc(a.short)} ›</a>`)
        .join('');
      this.box.innerHTML = `<div class="cw-sr__stack cw-sr__stack--results">
          <div class="cw-sr__count">${esc(count)}</div>
          ${links ? `<div class="cw-sr__dkaisles"><span>Aisles:</span>${links}</div>` : ''}
          <div class="cw-sr__rows" data-rows="results"></div>
          <a class="cw-sr__seeall" href="${root()}search?q=${encodeURIComponent(this.q)}" data-see-all>See all ${count} ›</a>
        </div>`;
      const rows = this.box.querySelector('[data-rows="results"]');
      if (wantsMix(this.q)) rows.append(mixRow());
      rows.append(...found.slice(0, 5).map(rowOf));
      return;
    }

    const chips =
      aisles.length >= 2
        ? `<div class="cw-sr__achips">${['', ...aisles]
            .map((id) => {
              const a = AISLES.find((x) => x.id === id);
              const on = (id || null) === (this.aisle || null);
              return `<button type="button" class="cw-sr__chip${on ? ' is-on' : ''}" data-aisle-filter="${esc(id)}" aria-pressed="${on}">${esc(a ? a.short : 'All')}</button>`;
            })
            .join('')}</div>`
        : '';
    const grid = !PHONE.matches;
    const top = `<div class="cw-sr__count">${esc(count)}</div>${chips}`;
    if (this.head) this.head.innerHTML = top;
    this.box.innerHTML = `<div class="cw-sr__stack cw-sr__stack--results">
        ${this.head ? '' : top}
        ${grid ? '<div class="cw-grid cw-sr__grid" data-rows="results"></div>' : '<div class="cw-sr__rows" data-rows="results"></div>'}
      </div>`;
    const list = this.box.querySelector('[data-rows="results"]');
    // The builder first, unless an aisle filter other than the mix's own is on.
    const mixAisle = document.getElementById('cw-mix-entry')?.dataset.aisle || 'atta';
    if (wantsMix(this.q) && (!this.aisle || this.aisle === mixAisle)) {
      const entry = grid ? mixTile().cloneNode(true) : mixRow();
      if (entry) list.append(entry);
    }
    if (grid) list.append(...shown.map((i) => i.card.cloneNode(true)));
    else list.append(...shown.map(rowOf));
    this.countLabel = count;
    this.aisles = aisles;
  }

  /** Clicks inside the states; returns a query to search for when one was picked. */
  onClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const picked = target.closest('[data-query]');
    if (picked) return picked.dataset.query;
    if (target.closest('[data-clear-recent]')) {
      saveList(RECENT_KEY, []);
      this.#idle();
      return null;
    }
    const filter = target.closest('[data-aisle-filter]');
    if (filter) {
      this.aisle = filter.dataset.aisleFilter || null;
      this.draw(this.q);
      return null;
    }
    if (target.closest('[data-request]')) {
      this.#request();
      return null;
    }
    if (target.closest('[data-result], [data-see-all]')) remember(this.q);
    return null;
  }

  /**
   * "REQUEST": signed in, it reaches the shop by its contact form; else sign in first. Signed in
   * is the customer id, not an email: a phone customer may have none of their own (their record
   * then carries the OTP app's stand-in, which the form takes; the name and number in the
   * message are how the shop answers).
   */
  async #request() {
    const { customer, customerEmail: email = '', customerName: name = '', customerPhone: phone = '' } = document.body.dataset;
    if (!customer || !email) {
      const ask = new CustomEvent('cw:signin:open', { cancelable: true });
      document.dispatchEvent(ask);
      if (!ask.defaultPrevented) window.location.href = `${root()}account/login`;
      return;
    }
    const who = [name, phone].map((s) => s.trim()).filter(Boolean).join(', ');
    const body = new FormData();
    body.append('form_type', 'contact');
    body.append('utf8', '✓');
    body.append('contact[email]', email);
    body.append('contact[body]', `Please stock: ${this.q} (asked from search${who ? ` by ${who}` : ''})`);
    try {
      await fetch(`${root()}contact`, { method: 'POST', body });
      saveList(REQ_KEY, [...loadList(REQ_KEY), this.q.toLowerCase()]);
      this.#none();
    } catch {
      // nothing sent; the button stays
    }
  }
}

/** Phone / tablet overlay and the desktop dropdown. */
class CwSearch extends HTMLElement {
  #view = null;
  #timer = null;
  #open = false;

  connectedCallback() {
    this.results = this.querySelector('[data-cw="results"]');
    this.input = this.querySelector('[data-cw="input"]');
    this.clear = this.querySelector('[data-cw="clear"]');
    this.headerInput = document.querySelector('.cw-hdr-d__input');
    this.#view = new SearchView(this.results, { mode: 'overlay' });
    document.addEventListener('cw:search:open', this.#onOpen);
    this.addEventListener('click', this.#onClick);
    this.input?.addEventListener('input', this.#onType);
    this.input?.addEventListener('keydown', this.#onKey);
    this.headerInput?.addEventListener('focus', this.#onHeaderFocus);
    this.headerInput?.addEventListener('input', this.#onType);
    this.headerInput?.addEventListener('keydown', this.#onKey);
    document.addEventListener('keydown', this.#onEsc);
    document.addEventListener('click', this.#onOutside, true);
    document.addEventListener('cw:cart:change', this.#basket);
    document.addEventListener('cw:cart:increase', this.#added);
    this.#basket();
  }

  disconnectedCallback() {
    document.removeEventListener('cw:search:open', this.#onOpen);
    document.removeEventListener('keydown', this.#onEsc);
    document.removeEventListener('click', this.#onOutside, true);
    document.removeEventListener('cw:cart:change', this.#basket);
    document.removeEventListener('cw:cart:increase', this.#added);
  }

  get #field() {
    return PHONE.matches ? this.input : this.headerInput || this.input;
  }

  #onOpen = (event) => {
    // On the results page the page itself is the search.
    if (document.body.dataset.template === 'search' && DESK.matches) return;
    event.preventDefault();
    this.show();
  };

  #onHeaderFocus = () => {
    if (document.body.dataset.template === 'search' && DESK.matches) return;
    this.show();
  };

  show() {
    this.#view.mode = DESK.matches ? 'dropdown' : 'overlay';
    this.dataset.mode = this.#view.mode;
    this.hidden = false;
    this.#open = true;
    document.documentElement.classList.toggle('cw-searching', !DESK.matches);
    document.body.classList.add('is-search-open');
    if (DESK.matches) this.#place();
    if (PHONE.matches) requestAnimationFrame(() => this.input?.focus());
    loadCatalogue().catch(() => {});
    this.#draw();
  }

  hide() {
    if (!this.#open) return;
    this.#open = false;
    this.hidden = true;
    document.documentElement.classList.remove('cw-searching');
    document.body.classList.remove('is-search-open');
  }

  /** Desktop: under the header's field (search.md: left 0, top 54 from the field's box). */
  #place() {
    const field = this.headerInput?.closest('.cw-hdr-d__search');
    if (!field) return;
    const r = field.getBoundingClientRect();
    this.style.left = `${Math.round(r.left)}px`;
    this.style.top = `${Math.round(r.top + 54)}px`;
  }

  #draw() {
    const q = this.#field?.value || '';
    this.clear?.toggleAttribute('hidden', !q);
    this.#view.draw(q);
  }

  #onType = (event) => {
    if (!this.#open) this.show();
    if (event.target === this.input && this.headerInput) this.headerInput.value = this.input.value;
    if (event.target === this.headerInput && this.input) this.input.value = this.headerInput.value;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#draw(), 120);
  };

  #onKey = (event) => {
    if (event.key !== 'Enter') return;
    const q = (event.target.value || '').trim();
    if (!q) return;
    event.preventDefault();
    remember(q);
    if (DESK.matches) window.location.href = `${root()}search?q=${encodeURIComponent(q)}`;
    else event.target.blur();
  };

  #onEsc = (event) => {
    if (event.key === 'Escape' && this.#open) this.hide();
  };

  /** Desktop dropdown: a click elsewhere closes it. */
  #onOutside = (event) => {
    if (this.#open && /** @type {HTMLElement} */ (event.target).closest?.('.cw-hdr-d__cancel')) {
      this.hide();
      return;
    }
    if (!this.#open || !DESK.matches) return;
    const target = /** @type {Node} */ (event.target);
    if (this.contains(target) || this.headerInput?.closest('.cw-hdr-d__search')?.contains(target)) return;
    this.hide();
  };

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-cw="back"], [data-cw="cancel"]')) return this.hide();
    if (target.closest('[data-cw="clear"]')) {
      for (const field of [this.input, this.headerInput]) if (field) field.value = '';
      this.#field?.focus();
      return this.#draw();
    }
    if (target.closest('[data-open="basket"]')) {
      this.hide();
      document.dispatchEvent(new CustomEvent('cw:basket:open', { cancelable: true }));
      return;
    }
    const q = this.#view.onClick(event);
    if (q) {
      for (const field of [this.input, this.headerInput]) if (field) field.value = q;
      this.#draw();
    }
  };

  /** A result added from the results: remember the search (behaviour.md §5). */
  #added = () => {
    if (this.#open && this.#view.q) remember(this.#view.q);
  };

  /** The overlay's own basket bar (phone). */
  #basket = () => {
    const bar = this.querySelector('[data-cw="sbar"]');
    if (!bar) return;
    const n = cart.count;
    bar.hidden = n === 0;
    bar.querySelector('[data-cw="sbar-n"]').textContent = `Basket · ${n} item${n === 1 ? '' : 's'}`;
    const total = cart.items.reduce((sum, item) => sum + (item.final_line_price ?? item.line_price ?? 0), 0);
    bar.querySelector('[data-cw="sbar-total"]').textContent = rupees(total);
  };
}

/** /search?q=: the results page (desktop), also a plain page on phones and tablets. */
class CwSearchPage extends HTMLElement {
  connectedCallback() {
    this.q = this.dataset.q || '';
    this.view = new SearchView(this.querySelector('[data-cw="page-results"]'), { mode: 'page', head: this.querySelector('[data-cw="page-head"]') });
    this.addEventListener('click', (event) => {
      const q = this.view.onClick(event);
      if (q) window.location.href = `${root()}search?q=${encodeURIComponent(q)}`;
      if (/** @type {HTMLElement} */ (event.target).closest('[data-cw="back"]') && document.referrer.startsWith(window.location.origin)) {
        event.preventDefault();
        window.history.back();
      }
    });
    remember(this.q);
    this.view.draw(this.q);
  }
}

if (!customElements.get('cw-search')) customElements.define('cw-search', CwSearch);
if (!customElements.get('cw-search-page')) customElements.define('cw-search-page', CwSearchPage);
